/**
 * KanbanBoardView — renders a Kanban board inline in chat messages.
 *
 * CSS Grid/Flexbox layout (no D3). Supports inline and fullscreen modes.
 * Columns scroll horizontally when they overflow. Cards show priority badges,
 * progress bars, assignee initials, due dates, and comment/attachment counts.
 *
 * Truncates visible cards to maxCardsPerColumn (default 8, cap 12). "isDone"
 * columns receive a subtle green tint with slightly muted card content.
 */

import type {
	KanbanChartConfig,
	KanbanColumn,
	KanbanCard,
	KanbanPriority,
} from "../../types/index.js";

import {
	avatarColour,
	initials,
	formatDate,
	PRIORITY_COLOURS,
	STATUS_COLOURS,
} from "./chartUtils.js";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface KanbanBoardViewProps {
	config: KanbanChartConfig;
	fullscreen?: boolean;
	isModal?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_MAX_CARDS = 8;
const ABSOLUTE_CAP = 12;
const MAX_VISIBLE_ASSIGNEES = 3;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Check whether a due date is in the past. Parses date-only strings as local midnight to avoid UTC offset shifting. */
function isOverdue(dueDate: string): boolean {
	const [year, month, day] = dueDate.split("-").map(Number);
	if (!year || !month || !day) return false;
	const d = new Date(year, month - 1, day);
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	return d < today;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PriorityBadge({ priority }: { priority: KanbanPriority }) {
	const colourKey = PRIORITY_COLOURS[priority] ?? "grey";
	const colours = STATUS_COLOURS[colourKey] ?? STATUS_COLOURS["grey"]!;
	return (
		<span
			className="kanban-priority-badge"
			style={{ background: colours.bg, color: colours.text }}
		>
			<span
				className="kanban-priority-dot"
				style={{ background: colours.dot }}
				aria-hidden="true"
			/>
			{priority}
		</span>
	);
}

function KanbanProgressBar({ value }: { value: number }) {
	const pct = Math.min(100, Math.max(0, value));
	return (
		<div className="kanban-progress">
			<div className="kanban-progress-track">
				<div
					className="kanban-progress-fill"
					style={{ width: `${pct}%` }}
					aria-hidden="true"
				/>
			</div>
			<span className="kanban-progress-label">{pct}%</span>
		</div>
	);
}

function AssigneeStack({ assignees }: { assignees: string[] }) {
	const visible = assignees.slice(0, MAX_VISIBLE_ASSIGNEES);
	const overflow = assignees.length - MAX_VISIBLE_ASSIGNEES;
	return (
		<div className="kanban-assignees">
			{visible.map((name) => (
				<span
					key={name}
					className="kanban-assignee-circle"
					style={{ background: avatarColour(name) }}
					title={name}
					aria-label={name}
				>
					{initials(name)}
				</span>
			))}
			{overflow > 0 && (
				<span
					className="kanban-assignee-circle kanban-assignee-overflow"
					title={`${overflow} more`}
					aria-label={`${overflow} more assignees`}
				>
					+{overflow}
				</span>
			)}
		</div>
	);
}

function CardFooter({
	card,
	isDone,
}: {
	card: KanbanCard;
	isDone: boolean;
}) {
	const hasAssignees = card.assignees && card.assignees.length > 0;
	const hasDueDate = !!card.dueDate;
	const hasComments = (card.commentCount ?? 0) > 0;
	const hasAttachments = (card.attachmentCount ?? 0) > 0;
	const hasFooterContent = hasAssignees || hasDueDate || hasComments || hasAttachments;

	if (!hasFooterContent) return null;

	const overdue = hasDueDate && !isDone && isOverdue(card.dueDate!);

	return (
		<div className="kanban-card-footer">
			{hasAssignees && <AssigneeStack assignees={card.assignees!} />}
			<div className="kanban-card-meta">
				{hasComments && (
					<span className="kanban-meta-item" title={`${card.commentCount} comments`}>
						<svg viewBox="0 0 14 14" width="12" height="12" fill="none" aria-hidden="true">
							<path d="M1 3a2 2 0 012-2h8a2 2 0 012 2v5a2 2 0 01-2 2H5l-3 2V3z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
						</svg>
						{card.commentCount}
					</span>
				)}
				{hasAttachments && (
					<span className="kanban-meta-item" title={`${card.attachmentCount} attachments`}>
						<svg viewBox="0 0 14 14" width="12" height="12" fill="none" aria-hidden="true">
							<path d="M7.5 2.5l-4 4a2.83 2.83 0 004 4l5-5a1.41 1.41 0 00-2-2l-5 5a.71.71 0 001 1l4-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
						{card.attachmentCount}
					</span>
				)}
				{hasDueDate && (
					<span
						className={`kanban-meta-item kanban-due-date${overdue ? " kanban-due-date--overdue" : ""}`}
						title={overdue ? "Overdue" : `Due ${formatDate(card.dueDate!)}`}
					>
						<svg viewBox="0 0 14 14" width="12" height="12" fill="none" aria-hidden="true">
							<rect x="1" y="2" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
							<path d="M1 5.5h12M4 1v2M10 1v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
						</svg>
						{formatDate(card.dueDate!)}
					</span>
				)}
			</div>
		</div>
	);
}

function KanbanCardItem({
	card,
	isDone,
	fullscreen,
	animationDelay,
}: {
	card: KanbanCard;
	isDone: boolean;
	fullscreen: boolean;
	animationDelay: number;
}) {
	return (
		<article
			className={`kanban-card${isDone ? " kanban-card--done" : ""}`}
			style={{ animationDelay: `${animationDelay}ms` }}
		>
			{/* Priority badge */}
			{card.priority && card.priority !== "None" && (
				<PriorityBadge priority={card.priority} />
			)}

			{/* Title */}
			<h4 className="kanban-card-title">{card.title}</h4>

			{/* Description (fullscreen only) */}
			{fullscreen && card.description && (
				<p className="kanban-card-description">{card.description}</p>
			)}

			{/* Progress */}
			{card.percentComplete !== undefined && card.percentComplete !== null && (
				<KanbanProgressBar value={card.percentComplete} />
			)}

			{/* Footer: assignees, due date, counts */}
			<CardFooter card={card} isDone={isDone} />
		</article>
	);
}

function KanbanColumnView({
	column,
	maxCards,
	fullscreen,
	columnIndex,
}: {
	column: KanbanColumn;
	maxCards: number;
	fullscreen: boolean;
	columnIndex: number;
}) {
	const totalCards = column.cards.length;
	const displayCards = column.cards.slice(0, maxCards);
	const overflow = totalCards - maxCards;

	return (
		<section
			className={`kanban-column${column.isDone ? " kanban-column--done" : ""}`}
			aria-label={`${column.title} — ${totalCards} tasks`}
		>
			{/* Column header with coloured accent stripe */}
			<div className="kanban-column-header">
				<span
					className="kanban-column-stripe"
					style={{ background: column.colour }}
					aria-hidden="true"
				/>
				<h3 className="kanban-column-title">
					{column.title}
					<span className="kanban-column-count">({totalCards})</span>
				</h3>
			</div>

			{/* Cards */}
			<div className="kanban-column-body">
				{totalCards === 0 ? (
					<p className="kanban-column-empty">No tasks</p>
				) : (
					<>
						{displayCards.map((card, cardIdx) => (
							<KanbanCardItem
								key={card.id}
								card={card}
								isDone={!!column.isDone}
								fullscreen={fullscreen}
								animationDelay={columnIndex * 60 + cardIdx * 30}
							/>
						))}
						{overflow > 0 && (
							<p className="kanban-column-overflow">
								+{overflow} more
							</p>
						)}
					</>
				)}
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function KanbanBoardView({ config, fullscreen = false, isModal: _isModal = false }: KanbanBoardViewProps) {
	const { columns, maxCardsPerColumn } = config;

	// Resolve effective card limit
	const limit = Math.min(maxCardsPerColumn ?? DEFAULT_MAX_CARDS, ABSOLUTE_CAP);

	// Empty state
	if (!columns || columns.length === 0) {
		return (
			<div className="chart-empty-state" role="status">
				No columns available
			</div>
		);
	}

	return (
		<div
			className={`kanban-board${fullscreen ? " kanban-board--fullscreen" : ""}`}
			role="region"
			aria-label="Kanban board"
		>
			{columns.map((column, idx) => (
				<KanbanColumnView
					key={column.id}
					column={column}
					maxCards={limit}
					fullscreen={fullscreen}
					columnIndex={idx}
				/>
			))}
		</div>
	);
}
